"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X, ChevronRight } from "lucide-react";
import { Logo } from "./Logo";
import { useAuthModal } from "@/auth/auth-modal-provider";
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "@repo/backend/convex/_generated/api";
import { useEffect, useState } from "react";
import { useBoard } from "@/components/BoardProvider";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

export function Header({ topClass }: { topClass?: string }) {
  const { openAuthModal } = useAuthModal();
  const { isAuthenticated } = useConvexAuth();
  const board = useBoard();
  const pathname = usePathname();
  // No strip on marketing routes any more, so the navbar sits flush at the top
  // unless a page asks for its own offset.
  const resolvedTop = topClass ?? "top-0";

  // Mentors tab is hidden by default; admins reveal it via the "mentors_tab_visible"
  // toggle in the admin mentors page once mentors are ready to go live. The toggle
  // is one global row, so keeping mentors off ESAT has to happen here.
  const mentorsEnabled =
    useQuery(api.siteSettings.getSetting, { key: "mentors_tab_visible" }) === true;
  const mentorsTabVisible = mentorsEnabled && board.slug !== "esat";

  const navLinks = [
    { href: "/features", label: "Features" },
    { href: "/textbook", label: "Textbook" },
    { href: "/past-papers", label: "Past Papers" },
    ...(board.slug === "esat" ? [{ href: "/articles", label: "Articles" }] : []),
    ...(mentorsTabVisible ? [{ href: "/mentors", label: "Mentors" }] : []),
    { href: "/scholarship", label: "Scholarships" },
    { href: "/faqs", label: "Got questions?" },
  ];

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile sidebar whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <header
      className={`w-full flex justify-center px-4 sticky z-50 py-2 md:py-4 ${resolvedTop}`}
    >
      <div
        className={`w-full rounded-full border transition-[max-width,padding,background-color,border-color,box-shadow,backdrop-filter] duration-300 ease-out ${
          scrolled
            ? "max-w-5xl px-2 md:px-6 bg-background/90 backdrop-blur-xl border-border shadow-[0_8px_24px_-12px_rgba(0,0,0,0.25)]"
            : "max-w-7xl px-3 md:px-8 bg-transparent border-transparent shadow-none"
        }`}
      >
        <nav className="w-full relative flex items-center gap-6 font-polysans text-sm md:text-base h-12 md:h-14">
          {/* Logo left */}
          <Logo />

          {/* Centre nav links (desktop only) */}
          <div className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2 whitespace-nowrap">
            {navLinks.map((link) => {
              const active = pathname === link.href;
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  aria-current={active ? "page" : undefined}
                  className={`px-3 py-1.5 rounded-full transition-colors duration-200 ${
                    active
                      ? "bg-muted text-foreground"
                      : "text-foreground/70 hover:bg-muted/60 hover:text-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Right cluster */}
          <div className="ml-auto flex items-center gap-2 md:gap-3">
            {isAuthenticated ? (
              <Link href="/dashboard">
                <button
                  type="button"
                  className="px-4 py-1.5 rounded-full bg-[var(--primary)] text-white shadow-md transition-[transform,box-shadow] duration-200 ease-out hover:shadow-lg motion-safe:active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/70"
                >
                  Dashboard
                </button>
              </Link>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => openAuthModal("signin")}
                  className="hidden sm:inline-flex px-4 py-1.5 rounded-full border border-border text-foreground/70 transition-[transform,background-color,color] duration-200 ease-out hover:bg-muted hover:text-foreground motion-safe:active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border"
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => openAuthModal("signup")}
                  className="px-3 md:px-4 py-1.5 rounded-full bg-primary text-primary-foreground shadow-md transition-[transform,box-shadow,background-color] duration-200 ease-out hover:bg-primary/90 hover:shadow-lg motion-safe:active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
                >
                  Sign Up
                </button>
              </>
            )}

            {/* Hamburger → sidebar (below lg) */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Open menu"
                  className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-border/60 text-foreground/80 transition-colors duration-200 hover:bg-muted hover:text-foreground motion-safe:active:scale-[0.94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[86%] max-w-sm gap-0 p-0 font-polysans"
              >
                <SheetHeader className="flex-row items-center justify-between px-5 h-16 border-b border-border/60 space-y-0">
                  <SheetTitle asChild>
                    <div>
                      <Logo />
                    </div>
                  </SheetTitle>
                  <SheetClose asChild>
                    <button
                      type="button"
                      aria-label="Close menu"
                      className="inline-flex items-center justify-center w-9 h-9 -mr-1.5 rounded-full text-foreground/60 transition-colors duration-200 hover:bg-muted hover:text-foreground motion-safe:active:scale-[0.94]"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </SheetClose>
                </SheetHeader>

                <nav className="flex-1 flex flex-col justify-center px-5">
                  {navLinks.map((link) => {
                    const active = pathname === link.href;
                    return (
                      <SheetClose asChild key={link.href}>
                        <Link
                          href={link.href}
                          aria-current={active ? "page" : undefined}
                          className={`group flex items-center justify-between py-3.5 border-b border-border/50 text-[1.0625rem] transition-colors duration-200 ${
                            active
                              ? "text-primary font-medium"
                              : "text-foreground/80 hover:text-foreground"
                          }`}
                        >
                          <span>{link.label}</span>
                          <ChevronRight
                            className={`w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 ${
                              active ? "text-primary" : "text-foreground/25"
                            }`}
                          />
                        </Link>
                      </SheetClose>
                    );
                  })}
                </nav>

                <div className="border-t border-border/60 px-5 pt-5 pb-6 flex flex-col gap-2.5">
                  {isAuthenticated ? (
                    <SheetClose asChild>
                      <Link
                        href="/dashboard"
                        className="w-full text-center px-4 py-2.5 rounded-2xl bg-[var(--primary)] text-white font-medium shadow-md transition-[transform,box-shadow] duration-200 ease-out hover:shadow-lg motion-safe:active:scale-[0.98]"
                      >
                        Dashboard
                      </Link>
                    </SheetClose>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          openAuthModal("signin");
                        }}
                        className="w-full px-4 py-2.5 rounded-2xl border border-border text-foreground/80 font-medium transition-colors duration-200 hover:bg-muted hover:text-foreground motion-safe:active:scale-[0.98]"
                      >
                        Log In
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          openAuthModal("signup");
                        }}
                        className="w-full px-4 py-2.5 rounded-2xl bg-primary text-primary-foreground font-medium shadow-md transition-[transform,box-shadow,background-color] duration-200 ease-out hover:bg-primary/90 hover:shadow-lg motion-safe:active:scale-[0.98]"
                      >
                        Sign Up
                      </button>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
}
